import Constant from '@/common/constant/Constant';
import LogKibanaConstant from '@/common/constant/LogKibanaConstant';
import Utils from '@/common/utils/utils';
import { getSystemFieldNameInDrillDown, getSystemFieldRawNameInDrillDown } from '@/component/ticketKnowledge/handler/DrilldownAndDetailSearchHandler';
import { processDrillDownDataForm, sortListTagFieldByOrder, sortListTagFieldBySysField } from '@/component/ticketKnowledge/handler/ListTicketKnowledgeHandler';
import {
    processDrillDownDataSuffixOfAttachments,
    processDrillDownDataTagField
} from '@/component/ticketList/handler/HelpDeskTicketListHandler';
import { getPublicAPI, publicGetKnowledgeListAPI } from '@/service/publicService';
import _ from 'lodash';
import { cloneDeep } from 'lodash';
import { createKibanaData } from '@/component/system/logKibanaHandler/LogKibanaHandler';
import { displayTagOfThumbAttach } from '@/component/ticketKnowledge/handler/ListTicketKnowledgeHandler';

// sysFields that should always appear at the end of the drilldown list
const SYS_FIELD_END_ORDER = [
    Constant.SYSTEM_FIELD.CHAPTER,
    Constant.SYSTEM_FIELD.SECTION,
    Constant.SYSTEM_FIELD.SUBSECTION,
    Constant.SYSTEM_FIELD.ITEM,
];

export const buildPublicKnowledgeUrl = (subDomain, projectId, pageParams, listViewTab, originParams) => {
    let { viewStructure, sortParams, selectedView, currentPage } = pageParams;
    if (!subDomain || !projectId) return;
    let url = buildDefaultPublicKnowledgeUrl(subDomain, projectId);
    let searchParams = '';
    let firstViewData = !Utils.isEmptyArray(listViewTab) ? listViewTab[0] : {};
    let firstViewStructure = Utils.convertJSONDataToObject(firstViewData.defaultStructure);

    if (selectedView && firstViewData.id !== selectedView) {
        searchParams = searchParams
            ? `${searchParams}&view=${selectedView}`
            : `view=${selectedView}`;
    }

    if (viewStructure?.keyword && firstViewStructure?.keyword !== viewStructure?.keyword) {
        searchParams = searchParams
            ? `${searchParams}&text=${viewStructure?.keyword}`
            : `text=${viewStructure?.keyword}`;
    }

    if (currentPage && currentPage !== 1) {
        searchParams = searchParams ? `${searchParams}&page=${currentPage}` : `page=${currentPage}`;
    }

    if (viewStructure?.ticketCreatedProjectId && firstViewStructure?.ticketCreatedProjectId !== viewStructure?.ticketCreatedProjectId) {
        searchParams = searchParams
            ? `${searchParams}&affiliationProject=${viewStructure?.ticketCreatedProjectId}`
            : `affiliationProject=${viewStructure?.ticketCreatedProjectId}`;
    } else if (!viewStructure?.ticketCreatedProjectId && originParams && originParams === Constant.CHATBOT_ORIGIN.CHATBOT) {
        searchParams = searchParams
            ? `${searchParams}&affiliationProject=""`
            : `affiliationProject=""`;
    }

    if (!Utils.isEmptyArray(viewStructure?.fields) && !_.isEqual(viewStructure?.fields?.sort(), firstViewStructure?.fields?.sort())) {
        let queryDrilldown = '';
        let uniqueParamsObject = {};
        viewStructure.fields.forEach(systemFieldData => {
            if (
                !Utils.isEmptyObject(systemFieldData) &&
                ((systemFieldData.hasOwnProperty('form') &&
                    !uniqueParamsObject.hasOwnProperty(Object.keys(systemFieldData)[0])) ||
                    systemFieldData.hasOwnProperty('suffixOfAttachments'))
            ) {
                let drilldownId = !Utils.isEmptyArray(Object.keys(systemFieldData))
                    ? Object.keys(systemFieldData)[0]
                    : '';
                let drilldownValue = systemFieldData[drilldownId];
                queryDrilldown = queryDrilldown
                    ? `${queryDrilldown}&${drilldownId}=${drilldownValue}`
                    : `${drilldownId}=${drilldownValue}`;
                uniqueParamsObject[drilldownId] = true;
            }
        });
        if (queryDrilldown) {
            searchParams = searchParams ? `${searchParams}&${queryDrilldown}` : `${queryDrilldown}`;
        }
    }

    let sortValue = Constant.DRILLDOWN_SEARCH_OPTION_SORT_TYPE.CREATED_AT;
    if (Utils.isEmptyString(sortParams)) {
        if (!Utils.isEmptyObject(viewStructure?.options?.sort))
            sortValue = viewStructure?.options?.sort;
        searchParams = searchParams ? `${searchParams}&sort=${sortValue.toLowerCase()}` : ``;
    } else if (firstViewStructure && (!firstViewStructure || !firstViewStructure.options || sortParams !== firstViewStructure?.options['sort'])) {
        sortValue = sortParams.toLowerCase();
        searchParams = searchParams ? `${searchParams}&sort=${sortValue}` : `sort=${sortValue}`;
    }
    
    // PDF file filtering parameters
    if (viewStructure?.options?.filePdfId) {
        searchParams = searchParams 
            ? `${searchParams}&filePdfId=${viewStructure.options.filePdfId}`
            : `filePdfId=${viewStructure.options.filePdfId}`;
        
        // Add fileName for chip display
        if (viewStructure?.fileNamePdfSelect) {
            const encodedFileName = encodeURIComponent(viewStructure.fileNamePdfSelect);
            searchParams = searchParams 
                ? `${searchParams}&fileName=${encodedFileName}`
                : `fileName=${encodedFileName}`;
        }
    }
    
    if (!Utils.isEmptyArray(viewStructure.tagFields) && (!firstViewStructure || !firstViewStructure.tagFields || Utils.isEmptyArray(firstViewStructure.tagFields) || !_.isEqual(viewStructure?.tagFields?.sort(), firstViewStructure?.tagFields?.sort()))) {
        let tagFieldParams = JSON.stringify(viewStructure.tagFields);
        searchParams = searchParams
            ? `${searchParams}&tag=${tagFieldParams}`
            : `tag=${tagFieldParams}`;
    }

    if(!Utils.isEmptyArray(viewStructure.sharedBookmark)){
        let sharedBookmarkParams = JSON.stringify(viewStructure.sharedBookmark);
        searchParams = searchParams
            ? `${searchParams}&sharedBookmark=${sharedBookmarkParams}`
            : `sharedBookmark=${sharedBookmarkParams}`;
    }

    if(!Utils.isEmptyArray(viewStructure.privateBookmark)){
        let privateBookmarkParams = JSON.stringify(viewStructure.privateBookmark);
        searchParams = searchParams
            ? `${searchParams}&privateBookmark=${privateBookmarkParams}`
            : `privateBookmark=${privateBookmarkParams}`;
    }

    if (originParams && originParams === Constant.CHATBOT_ORIGIN.CHATBOT) {
        searchParams = searchParams
        ? `${searchParams}&origin=${originParams}`
        : `origin=${originParams}`;
    }

    if (searchParams) {
        url = `${url}?${searchParams}`;
    }
    url = encodeURI(url);

    return url;
};

const buildDefaultPublicKnowledgeUrl = (subDomain, projectId) => {
    return `${subDomain}/${projectId}/kb`;
    // return `${process.env.REACT_APP_DOMAIN_NAME}${subDomain}/${projectId}/kb`;
};

export const mappingDrillDownPublicKnowledge = (currentDrillDown, newDrillDown) => {
    if (!Utils.isEmptyObject(currentDrillDown) && !Utils.isEmptyObject(newDrillDown)) {
        for (const key of Object.keys(currentDrillDown)) {
            if (key === 'sortLastValue') continue;
            if (newDrillDown[key]['total']) {
                currentDrillDown[key]['total'] += newDrillDown[key]['total'];
            }
            switch (key) {
                case 'form': {
                    const currentChildData = currentDrillDown[key];
                    const newChildData = newDrillDown[key];
                    for (const childKey of Object.keys(newChildData)) {
                        if (currentChildData[childKey]) {
                            if (
                                currentChildData[childKey]['count'] &&
                                newChildData[childKey]['count']
                            ) {
                                currentChildData[childKey]['count'] +=
                                    newChildData[childKey]['count'];
                            }
                        }
                        if (!currentChildData[childKey]) {
                            currentChildData[childKey] = newChildData[childKey];
                        }
                    }
                    break;
                }
                case 'suffixOfAttachments': {
                    if (newDrillDown[key]['data']) {
                        if (!currentDrillDown[key]['data']) {
                            currentDrillDown[key]['data'] = newDrillDown[key]['data'];
                        } else {
                            for (const childKey of Object.keys(newDrillDown[key]['data'])) {
                                if (currentDrillDown[key]['data'][childKey]) {
                                    currentDrillDown[key]['data'][childKey] +=
                                        newDrillDown[key]['data'][childKey];
                                } else {
                                    currentDrillDown[key]['data'][childKey] =
                                        newDrillDown[key]['data'][childKey];
                                }
                            }
                        }
                    }
                    break;
                }
                case 'tag': {
                    const currentChildData = currentDrillDown[key];
                    const newChildData = newDrillDown[key];
                    for (const childKey of Object.keys(newChildData)) {
                        if (!currentChildData[childKey]) {
                            currentChildData[childKey] = newChildData[childKey];
                        }
                        if (currentChildData[childKey]) {
                            if (
                                currentChildData[childKey]['total'] &&
                                newChildData[childKey]['total']
                            ) {
                                currentChildData[childKey]['total'] +=
                                    newChildData[childKey]['total'];
                            }
                        }
                    }
                    break;
                }
                default:
                    break;
            }
        }
    }
    return currentDrillDown;
};

/**
 * getDrilldownPublicKnowledgeWithPagination
 * @param {*} API 
 * @param {*} filterParams 
 * @param {*} drillDown 
 * @returns 
 */
export const getDrilldownPublicKnowledgeWithPagination = async (API, filterParams, drillDown = {}) => {
    let queryData = await publicGetKnowledgeListAPI(API, filterParams);
    let listData = Utils.convertJSONDataToObject(queryData?.payload);
    let listDrillDown = listData;
    if (!Utils.isEmptyObject(drillDown)) {
        listDrillDown = mappingDrillDownPublicKnowledge(drillDown, listData);
    }
    // if (listData?.sortLastValue?.length) {
    //     const newFilterParams = { ...filterParams, searchAfter: listData?.sortLastValue };
    //     listDrillDown = await getDrilldownPublicKnowledgeWithPagination(
    //         API,
    //         newFilterParams,
    //         listDrillDown
    //     );
    // }
    return listDrillDown;
};

export const getDrilldownDataAndSugguestionList = async (
    API,
    subDomain,
    projectId,
    viewStructure,
    setDrillDownData,
    setSugguestionList
) => {
    let response = {
        drillDownData: [],
        sugguestionList: []
    };
    let params = {
        getApiType: Constant.GET_API_PUBLIC_TYPE.GET_PUBLIC_KNOWLEDGE_DRILLDOWN_DATA,
        subDomain,
        projectAlias: projectId,
        structure: buildParamsGetDrilldown(viewStructure),
        // size: 2000
    };
    let result = await getDrilldownPublicKnowledgeWithPagination(API, params);
    let drilldownData = Utils.convertJSONDataToObject(result);
    let formatDataResult = formatDrilldownDataAndSugguestionList(viewStructure, drilldownData);
    response.drillDownData = formatDataResult.drillDownData;
    response.sugguestionList = formatDataResult.sugguestionList;
    setDrillDownData(response.drillDownData);
    setSugguestionList(response.sugguestionList);

    return response;
};

const buildParamsGetDrilldown = viewStructure => {
    let params = Constant.DEFAULT_VIEW_STRUCTURE;
    if (viewStructure && !Utils.isEmptyObject(viewStructure)) {
        return viewStructure;
    }
    return params;
};

const formatDrilldownDataAndSugguestionList = (viewStructure, drilldownData) => {
    let data = [];
    let arrCustomFieldTag = [];
    let optionsList = [];

    if (!Utils.isEmptyObject(drilldownData)) {
        for (const [key, value] of Object.entries(drilldownData)) {
            if (!Utils.isEmptyObject(value)) {
                if (['form', 'suffixOfAttachments'].includes(key) && value.total) {
                    let processData;
                    let child = [];

                    switch (key) {
                        case 'form':
                            processData = processDrillDownDataForm(viewStructure, key, value);
                            break;
                        case 'suffixOfAttachments':
                            processData = processDrillDownDataSuffixOfAttachments(
                                viewStructure,
                                key,
                                value
                            );
                            break;
                        default:
                            break;
                    }

                    child = processData?.child || [];
                    if (!Utils.isEmptyArray(processData?.optionsList)) {
                        optionsList = optionsList.concat(processData?.optionsList);
                    }
                   
                    data.push({
                        id: key,
                        label: getSystemFieldNameInDrillDown(key),
                        rawLabel: getSystemFieldRawNameInDrillDown(key),
                        total: value.total,
                        usage: Constant.DRILLDOWN_USAGE.SYSTEM,
                        child,
                        isShowMore: false,
                        orderInList: value.orderInList
                    });
                    
                } else if (key === Constant.FORMAT_OPTION_SEARCH.TAG_FIELD) {
                    for (const [fieldId, fieldData] of Object.entries(value)) {
                        if (fieldData.total && !Utils.isEmptyArray(fieldData.itemList)) {
                            let customFieldData = { ...fieldData, type: key };
                            arrCustomFieldTag.push(customFieldData);
                        }
                    }
                }
            }
        }
    }

    // Sort here
    arrCustomFieldTag = arrCustomFieldTag.sort((a, b) => {
        return new Date(b.data.createdAt) - new Date(a.data.createdAt);
    });
    // Arrange tag field order according to the order in the tag fields list
    arrCustomFieldTag = sortListTagFieldByOrder(arrCustomFieldTag);
    // Arrange the order of card systems before custom cards
    arrCustomFieldTag = sortListTagFieldBySysField(arrCustomFieldTag);
    if (!Utils.isEmptyArray(arrCustomFieldTag)) {
        let processDataTagFieldResult = processDrillDownDataTagField(
            arrCustomFieldTag,
            viewStructure,
            data,
            optionsList
        );
        data = processDataTagFieldResult.data;
        optionsList = processDataTagFieldResult.optionsList;
    };
    
    data = data.sort((a, b) => {
        const aIndex = SYS_FIELD_END_ORDER.indexOf(a.sysField);
        const bIndex = SYS_FIELD_END_ORDER.indexOf(b.sysField);
        const aIsEndField = aIndex !== -1;
        const bIsEndField = bIndex !== -1;

        // Both are end fields - sort by predefined order
        if (aIsEndField && bIsEndField) {
            return aIndex - bIndex;
        }
        // Only a is end field - push to end
        if (aIsEndField) return 1;
        // Only b is end field - push to end
        if (bIsEndField) return -1;
        // Neither is end field - sort by orderInList (handle undefined)
        const aOrder = a.orderInList ?? Infinity;
        const bOrder = b.orderInList ?? Infinity;
        return aOrder - bOrder;
    });
    return { drillDownData: data, sugguestionList: optionsList };
};

const getSortName = sortType => {
    let sortName = '';
    switch (sortType) {
        case Constant.DRILLDOWN_SEARCH_OPTION_SORT_TYPE.UPDATED_AT:
            sortName = Constant.PUBLIC_KNOWLEDGE_SORT_TYPE.UPDATED_AT;
            break;
        case Constant.DRILLDOWN_SEARCH_OPTION_SORT_TYPE.CREATED_AT:
            sortName = Constant.PUBLIC_KNOWLEDGE_SORT_TYPE.CREATED_AT;
            break;
        case Constant.DRILLDOWN_SEARCH_OPTION_SORT_TYPE.SORT_TOTAL_PRIVATE_BOOKMARK_ASC:
            sortName = Constant.PUBLIC_KNOWLEDGE_SORT_TYPE.TOTAL_PRIVATE_BOOKMARK_ASC;
            break;
        case Constant.DRILLDOWN_SEARCH_OPTION_SORT_TYPE.DOC_BODY_DESC:
            sortName = Constant.PUBLIC_KNOWLEDGE_SORT_TYPE.TOTAL_PRIVATE_BOOKMARK_DESC;
            break;
        case Constant.DRILLDOWN_SEARCH_OPTION_SORT_TYPE.PAGE_NO:
            sortName = Constant.PUBLIC_KNOWLEDGE_SORT_TYPE.PAGE_UNIT_ASC;
            break;
        default:
            sortName = Constant.PUBLIC_KNOWLEDGE_SORT_TYPE.UPDATED_AT;
            break;
    }

    return sortName;
};

export const getPublicKnowledgeList = async (
    API,
    subDomain,
    projectId,
    viewStructure,
    page,
    isGetTotalItem,
    lastUserAction, 
    currentCorporationDetail,
    projectInuseDetail,
    userDetail,
    searchContentType,
    domain,
    selectedView,
    searchTagSuggest,
    removedTagItem,
    setRemovedTagItem,
    setSearchTagSuggest
) => {
    const publicKnowledgeListParams = {
        getApiType: Constant.GET_API_PUBLIC_TYPE.GET_PUBLIC_KNOWLEDGE_LIST,
        subDomain,
        projectId,
        viewStructure,
        page,
        isGetTotalItem
    };
    let keywordListString = '';

    if (viewStructure?.keyword) {
        keywordListString = viewStructure.keyword.trim();
    }
    if (lastUserAction) {
        let keywordList = keywordListString ? keywordListString.split(' ') : [];
        let filterInput = {
            optionSearch: {
                methodSearch: '',
                attributeSearch:''
            },
            affiliationOrganization: currentCorporationDetail.affiliationOrganization ? currentCorporationDetail.affiliationOrganization : '',
            affiliationProject: projectInuseDetail.id ? projectInuseDetail.id : projectId,
        }
        let user = {
            id: LogKibanaConstant.DUMMY_TEXT,
            mail:  '',
            lastName: '',
            firstName: '',
        }
        let corporationDetail = {
            organizationDomain: currentCorporationDetail.organizationDomain ? currentCorporationDetail.organizationDomain : domain,
            corporationName: currentCorporationDetail.corporationName ? currentCorporationDetail.corporationName : ''
        }
        let projectDetail = {
            name: projectInuseDetail.name ? projectInuseDetail.name : ''
        }
        let operationScreen = LogKibanaConstant.OPERATION_SCREEN.PUBLIC;
        let inforSearch = {
            searchDateType: '',
            fromSearchDate: '',
            toSearchDate: '',
            searchDateAgoValue: '',
            searchDateAgoUnit: '',
            searchContainAny: '',
            searchNotInclude: '',
            sortType: getSortName(viewStructure.options['sort']),
            
            // data for view change
            viewName: selectedView,
            viewId: selectedView,
            viewKeyword: keywordList,
            viewKeywordCondition: '',
            viewSearchTarget: '',
            viewDateType: '',
            viewDateFrom: '',
            viewDateTo: '',
            viewDateTerm: '',
            viewDateUnit: '',  
            viewOrTag: '',// get on server
            viewExceptTag: '',// get on server
            viewTag: '', // get on server
            viewSort: getSortName(viewStructure.options['sort']),
        }
        let logKibanaData;
        switch (lastUserAction) {
            case LogKibanaConstant.SEARCH_ACTION.TAG_REMOVE: {
                if (Utils.isEmptyObject(removedTagItem)) break;

                let tagRemove = `${removedTagItem.tagName}@${removedTagItem.fieldName}`;
                let newListTagSuggest = [];
                if (!Utils.isEmptyArray(searchTagSuggest)) {
                    for (let i = 0; i < searchTagSuggest.length; i++) {
                        if (tagRemove !== searchTagSuggest[i]) {
                            newListTagSuggest.push(searchTagSuggest[i]);
                        }
                    }
                }
                inforSearch.viewTag = newListTagSuggest;
                setSearchTagSuggest(newListTagSuggest);
                logKibanaData = createKibanaData(
                    lastUserAction,
                    filterInput,
                    keywordList,
                    corporationDetail,
                    projectDetail,
                    user,
                    searchContentType,
                    operationScreen,
                    newListTagSuggest,
                    inforSearch,
                    removedTagItem
                );

                //clear state click remove tag
                setRemovedTagItem({});
                break;
            }
            default:
                logKibanaData = createKibanaData(
                    lastUserAction,
                    filterInput,
                    keywordList,
                    corporationDetail,
                    projectDetail,
                    user,
                    searchContentType,
                    operationScreen,
                    searchTagSuggest,
                    inforSearch
                );
        }
        if(!Utils.isEmptyObject(logKibanaData)) publicKnowledgeListParams.logKibanaData = JSON.stringify(logKibanaData);
    }
    const publicKnowledgeList = await publicGetKnowledgeListAPI(API, publicKnowledgeListParams);
    return publicKnowledgeList;
};

export const initViewAndStructure = (parameters, viewList, pageParams, options = {}) => {
    const {
        setPageParams,
        listViewTab,
        setListViewTab,
        setIsShow404Page,
        setIsDoneInitView,
        isViewFromChatBot = false,
        publicKnowledgeSiteSetting
    } = options;

    if (Utils.isEmptyArray(viewList)) {
        setIsShow404Page(true);
        return;
    }
    
    let viewListClone = cloneDeep(viewList);
    viewListClone = viewListClone.map(view => {
        view.defaultStructure = cloneDeep(view.structure);

        if (!Utils.isEmptyArray(listViewTab)) {
            let oldViewData = listViewTab.find(oldView => oldView.id === view.id);
            if (!Utils.isEmptyObject(oldViewData) && oldViewData.updatedAt === view.updatedAt) {
                view.structure = oldViewData.structure;
            }
        }

        // let currentViewStructure = view.structure;
        // currentViewStructure.fields = [];
        // currentViewStructure.tagFields = [];
        // currentViewStructure.keyword = '';
        // view.structure = currentViewStructure;
        return view;
    });

    if (Utils.isEmptyArray(viewListClone)) {
        setIsShow404Page(true);
        return;
    }

    viewListClone = sortListViewByOrder(viewListClone);

    let selectedView = parameters.get('view');
    let isExistsNewArrivedView = false;
    let indexSelectedViewExistInViewList;

    if (selectedView) {
        indexSelectedViewExistInViewList = viewListClone.find(view => view.id === selectedView);
    }
    
    if (!selectedView || !indexSelectedViewExistInViewList) {
        selectedView = viewListClone[0].id;
        if (isViewFromChatBot) {
            let newArrivedView = viewListClone.find(view => view.viewType === Constant.VIEW_TYPE.DEFAULT);
            if (newArrivedView && !Utils.isEmptyObject(newArrivedView)) {
                selectedView = newArrivedView.id;
                isExistsNewArrivedView = true;
            }
        }
    }
    if (!selectedView) {
        setIsShow404Page(true);
        return;
    }

    let selectedViewDetail = viewListClone.find(view => view.id === selectedView);
    if (!selectedViewDetail || Utils.isEmptyObject(selectedViewDetail)) {
        setIsShow404Page(true);
        return;
    }

    setListViewTab(cloneDeep(viewListClone));

    let newPageParams = cloneDeep(pageParams);
    newPageParams.selectedView = selectedView;

    let viewStructure = Utils.convertJSONDataToObject(selectedViewDetail.structure);
    if (isViewFromChatBot && !isExistsNewArrivedView) {
        let defaultViewStructure = cloneDeep(Constant.DEFAULT_VIEW_STRUCTURE);
        delete defaultViewStructure.visibility;
        viewStructure = defaultViewStructure;
    }
    let fieldStructure = !Utils.isEmptyArray(viewStructure.fields)
        ? cloneDeep(viewStructure.fields)
        : [];

    // Parse PDF filtering from URL
    let filePdfIdParam = parameters.get('filePdfId');
    let fileNameParam = parameters.get('fileName');

    if (filePdfIdParam) {
        if (!viewStructure.options) {
            viewStructure.options = {};
        }
        // When PDF filter is applied from URL but view is not yet in PDF page filter mode,
        // exclude fields with form config from field structure so only PDF page context is shown.
        const viewSettingFilterByForm = viewStructure.fields?.filter(field => field.hasOwnProperty('form'));
        const isViewSettingFilterPage = !Utils.isEmptyString(viewStructure?.options?.filePdfId);
        if (!Utils.isEmptyArray(viewSettingFilterByForm) && !isViewSettingFilterPage) {
            fieldStructure = fieldStructure?.filter(field => !field.hasOwnProperty('form'));
        }

        viewStructure.options['filePdfId'] = filePdfIdParam;
        viewStructure.options['getListPdfPage'] = Constant.STATUS_SERVICE_PERMISSION.ON;

        // Force sort to page_no when viewing PDF pages
        viewStructure.options['sort'] = Constant.DRILLDOWN_SEARCH_OPTION_SORT_TYPE.PAGE_NO;

        // Set fileName for chip display
        if (fileNameParam) {
            viewStructure.fileNamePdfSelect = decodeURIComponent(fileNameParam);
        }
    }

    // Update drilldown form;
    let formParams = parameters.get('form');
    if (formParams || formParams === '') {
        fieldStructure = fieldStructure.filter(field => !field.hasOwnProperty('form'));
        if (formParams) {
            fieldStructure.push({ form: formParams });
        }
    }

    // Update drilldown suffixOfAttachments
    let suffixParams = parameters.getAll('suffixOfAttachments');
    if (!Utils.isEmptyArray(suffixParams)) {
        fieldStructure = fieldStructure.filter(
            field => !field.hasOwnProperty('suffixOfAttachments')
        );
        suffixParams.forEach(suffixParamsChild => {
            if (suffixParamsChild) {
                fieldStructure.push({ suffixOfAttachments: suffixParamsChild });
            }
        });
    }

    viewStructure.fields = fieldStructure;

    // Tag field data
    let tagFieldParam = parameters.get('tag');
    if (tagFieldParam) {
        let tagParams = Utils.convertJSONDataToObject(tagFieldParam);
        if (!Utils.isEmptyArray(tagParams)) {
            viewStructure.tagFields = tagParams;
        }
    }

    // Keyword search
    let keywordParams = parameters.get('text');
    if (keywordParams) {
        viewStructure.keyword = keywordParams;
    }

    let affiliationProjectParams = parameters.get('affiliationProject');
    if (affiliationProjectParams && affiliationProjectParams !== '""') {
        viewStructure.ticketCreatedProjectId = affiliationProjectParams;
    } else if (affiliationProjectParams === '""') {
        viewStructure.ticketCreatedProjectId = '';
    }

    // share bookmark
    let sharedBookmark = parameters.get('sharedBookmark');
    if (sharedBookmark) {
        let sharedBookmarkParams = Utils.convertJSONDataToObject(sharedBookmark);
        if (!Utils.isEmptyArray(sharedBookmarkParams)) {
            viewStructure.sharedBookmark = sharedBookmarkParams;
        }
    }

    // share bookmark
    let privateBookmark = parameters.get('privateBookmark');
    if (privateBookmark) {
        let privateBookmarkParams = Utils.convertJSONDataToObject(privateBookmark);
        if (!Utils.isEmptyArray(privateBookmarkParams)) {
            viewStructure.privateBookmark = privateBookmarkParams;
        }
    }

    newPageParams.viewStructure = viewStructure;

    let sortParams = newPageParams.sortParams;
    if (!Utils.isEmptyString(sortParams)) {
        switch (sortParams.toUpperCase()) {
            case Constant.DRILLDOWN_SEARCH_OPTION_SORT_TYPE.CREATED_AT:
                newPageParams.sortType = Constant.PUBLIC_KNOWLEDGE_SORT_TYPE.CREATED_AT;
                break;
            case Constant.DRILLDOWN_SEARCH_OPTION_SORT_TYPE.PAGE_NO:
                /** sort type page no, only existed when filter list by pdf page. So, checking configuration on pdf
                 * function, it's ON, we will display sort option page no and disable it. Or not when disabled,
                 * default option sort is updated at.
                 */
                if (publicKnowledgeSiteSetting?.isConvertPdfEnabled) {
                    newPageParams.sortType = Constant.PUBLIC_KNOWLEDGE_SORT_TYPE.PAGE_UNIT_ASC;
                } else {
                    newPageParams.sortType = Constant.PUBLIC_KNOWLEDGE_SORT_TYPE.UPDATED_AT;
                }
                break;
            default:
                newPageParams.sortType = Constant.PUBLIC_KNOWLEDGE_SORT_TYPE.UPDATED_AT;
                break;
        }
    } else {
        switch (selectedViewDetail.structure.options.sort) {
            case Constant.DRILLDOWN_SEARCH_OPTION_SORT_TYPE.CREATED_AT:
                newPageParams.sortType = Constant.PUBLIC_KNOWLEDGE_SORT_TYPE.CREATED_AT;
                break;
            case Constant.DRILLDOWN_SEARCH_OPTION_SORT_TYPE.PAGE_NO:
                newPageParams.sortType = Constant.PUBLIC_KNOWLEDGE_SORT_TYPE.PAGE_UNIT_ASC;
                break;
            // case Constant.DRILLDOWN_SEARCH_OPTION_SORT_TYPE.SORT_TOTAL_PRIVATE_BOOKMARK_ASC:
            //     newPageParams.sortType =
            //         Constant.PUBLIC_KNOWLEDGE_SORT_TYPE.TOTAL_PRIVATE_BOOKMARK_ASC;
            //     break;
            // case Constant.DRILLDOWN_SEARCH_OPTION_SORT_TYPE.SORT_TOTAL_PRIVATE_BOOKMARK_DESC:
            //     newPageParams.sortType =
            //         Constant.PUBLIC_KNOWLEDGE_SORT_TYPE.TOTAL_PRIVATE_BOOKMARK_DESC;
            //     break;
            default:
                newPageParams.sortType = Constant.PUBLIC_KNOWLEDGE_SORT_TYPE.UPDATED_AT;
                break;
        }
    }

    setPageParams(newPageParams);
    setIsDoneInitView(true);
};

export const getDefaultDataForChatBot = (drawDrillDownData, viewStructure) => {
    let drillDownData = [];

    let formatDataResult = formatDrilldownDataAndSugguestionList(viewStructure, drawDrillDownData);
    drillDownData = formatDataResult.drillDownData;

    let tagFieldList = [];
    let tagFieldIdList = [];

    drillDownData?.forEach(drillDownItem => {
        if (drillDownItem.usage === Constant.FORM_TYPE.FIELD_TAG) {
            tagFieldList.push(drillDownItem);
            tagFieldIdList.push(drillDownItem.id);
        }
    });

    return {
        tagFieldList,
        tagFieldIdList
    };
};

export const getPublicKnowledgeListForChatBot = async (
    API,
    corporationId,
    projectId,
    tagFieldQuery,
    page,
    isGetTotalItem,
    maxNumberAnswer,
    structure
) => {
    // tagFieldQuery= [];
    const publicKnowledgeListParams = {
        getApiType: Constant.GET_API_PUBLIC_TYPE.GET_LIST_PUBLIC_KNOWLEDGE_CHAT_BOT,
        corporationId: corporationId,
        projectId,
        tagFieldQuery,
        page,
        isGetTotalItem,
        idRequest: `debug_id${new Date().getTime()}`,
        maxNumberAnswer,
        structure
    };
    const response = await getPublicAPI(API, publicKnowledgeListParams);
    return response;
};

const sortListViewByOrder = (listView) => {
    return listView.sort((viewA, viewB) => {
        return viewA.order - viewB.order;
    });
};


/**
 * Build view structure for PDF page viewing
 * @param {Object} ticketDetail - Ticket detail object
 * @param {Object} fileInfo - File information object
 * @returns {Object} - New view structure configured for PDF pages
 */
const buildPdfViewStructure = (ticketDetail, fileInfo) => {
    const originStructure = {
        ...Constant.DEFAULT_VIEW_STRUCTURE,
        visibility: Constant.VISIBILITY_SETTING_TYPE.CUSTOMER
    };
    const newViewStructure = cloneDeep(originStructure);

    // Configure view to show PDF pages
    newViewStructure.options['sort'] = Constant.DRILLDOWN_SEARCH_OPTION_SORT_TYPE.PAGE_NO;
    newViewStructure.options['getListPdfPage'] = Constant.STATUS_SERVICE_PERMISSION.ON;
    
    // Set filePdfId based on ticket type
    if (
        ticketDetail.ticketType === Constant.TICKET_TYPE.KNOWLEDGE ||
        ticketDetail.ticketType === Constant.TICKET_TYPE.MASTER_KNOWLEDGE
    ) {
        newViewStructure.options['filePdfId'] = fileInfo.idTicketFileKnowledge;
    }
    if (ticketDetail.ticketType === Constant.TICKET_TYPE.PDF_FILE_KNOWLEDGE) {
        newViewStructure.options['filePdfId'] = ticketDetail.id;
    }
    if (ticketDetail.ticketType === Constant.TICKET_TYPE.PDF_PAGE_KNOWLEDGE) {
        newViewStructure.options['filePdfId'] = ticketDetail.parent;
    }

    return newViewStructure;
};

/**
 * Handle PDF file selection for public knowledge
 * @param {Object} params - Configuration object containing all necessary parameters
 */
export const handleSelectFilePdfPublic = (params) => {
    const {
        setPreviousSearchState,
        setViewStructure,
        viewStructure,
        pageParams,
        ticketDetail,
        fileInfo,
        isConvertPdfEnabled,
        setIsLoading,
        setTotalPage,
        setPublicKnowledgeEachPage
    } = params;

    setIsLoading(true);
    // Store search state for back navigation
    if (setPreviousSearchState) {
        setPreviousSearchState({
            pageParams: pageParams,
            viewStructure: viewStructure
        });
    }
    
    // Reset pagination for new PDF page view
    setTotalPage({ page: 0 });
    setPublicKnowledgeEachPage(Constant.PUBLIC_KNOWLEDGE_PAGE.PAGE_SIZE);
    // Validate PDF permission
    if (!isConvertPdfEnabled) {
        setIsLoading(false);
        return;
    }

    // If already viewing PDF pages, just switch to different file
    if (viewStructure?.options?.filePdfId) {
        // Update viewStructure with new fileName
        const newViewStructure = buildPdfViewStructure(ticketDetail, fileInfo);
        newViewStructure.fileNamePdfSelect = fileInfo.fileName;
        setViewStructure(newViewStructure);
        setIsLoading(false);
        return;
    }

    // Build new view structure for PDF pages
    const newViewStructure = buildPdfViewStructure(ticketDetail, fileInfo);
    newViewStructure.fileNamePdfSelect = fileInfo.fileName;

    setViewStructure(newViewStructure);
    setIsLoading(false);
};

export const isShowIconPageUnit = (
    ticketKnowledge,
    file,
    viewStructure,
    isConvertPdfEnabled
) => {
    if (
        [Constant.TICKET_TYPE.KNOWLEDGE, Constant.TICKET_TYPE.MASTER_KNOWLEDGE].includes(
            ticketKnowledge.ticketType
        ) &&
        !file?.idTicketFileKnowledge
    ) {
        return false;
    }
    
    const displayTextInFile = displayTagOfThumbAttach(ticketKnowledge, file.fileName);
    const isDisplayPdf = displayTextInFile.name === 'pdf';
    return (
        isDisplayPdf &&
        file.splitPdf &&
        !file.isCreatingPageKnowledge &&
        viewStructure?.ticketProperty !== Constant.TICKET_PROPERTY.SYNONYM &&
        ticketKnowledge?.ticketType !== Constant.TICKET_TYPE.PDF_PAGE_KNOWLEDGE &&
        isConvertPdfEnabled
    );
};
